import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { UserInfoContext } from '../user/user-info';
import { AccountInfoContext } from '../account/account-info';
import { OrgInfoContext } from '../organization/org-info';
import { tcrBrandSelector, tcrCampaignsSelector } from '@/redux/app/selectors/tcr';
import { orgIdSelector, orgPhoneNumbersSelector, orgTrialSurveyNumberTypeSelector } from '@/redux/app/selectors/organization';
import { getOrgProps } from '@/redux/app/actions/organization';
import { fetchTcrProps } from '@/redux/app/actions/tcr';
import { useNavigationActions } from '@/lib/navigation';
import { TrialPhoneVerifyModal } from '../trial/TrialPhoneVerifyModal';

/*
  Funnel-aware top-bar pill. Replaces the old "Your trial ends in X days.
  [Upgrade]" pill with a state machine that surfaces the most-valuable
  next action for each stage of the trial funnel:

    1. verify-phone   → user hasn't verified phone yet            (MQL gate)
    2. apply-a2p      → phone verified, no TCR brand registered   (SQL gate)
    3. a2p-pending    → A2P brand submitted, awaiting carrier review
    4. a2p-approved   → CNP-verified (external_campaign_status === 'VERIFIED')
                         → push trial users to upgrade
    5. paid-choose-number → upgraded but no number provisioned yet
    6. expired        → trial days <= 0, generic "Get a better rate"
    7. hide           → on paid plan with a provisioned number — nothing to push

  Note on Canada: per product, Canadian-targeting orgs should still go
  through A2P (it'll be a compliance requirement). No CA-specific branch
  in this header.

  Note on TFN: when the user picked 'tfn' as their number-type preference
  in Step 2 of the trial survey, we SKIP the A2P stages entirely (apply-
  a2p, a2p-pending, a2p-approved) and route them straight to 'upgrade-
  for-tfn'. The TFN compliance form (/accounts/:subdomain/tfn) embeds
  Twilio's compliance widget and requires a provisioned TFN number with
  a fresh inquiryId — which trial accounts don't have. The verify-TFN
  button surfaces in the org's number list *after* the number is bought,
  so the funnel for these users is: verify-phone → upgrade → choose TFN
  → (verify TFN appears on the number row).

  Phone verification: clicking "Verify Phone" opens `TrialPhoneVerifyModal`
  rendered inline below — the modal isn't globally auto-opened on /tickets
  (TicketView only triggers it on a failed send attempt), so the trial
  header owns its own instance to guarantee the CTA produces a modal
  regardless of which page the user is on.

  Live state transitions: hasTcrBrand, tcr campaigns, and phone numbers
  all read from redux selectors, so submit-A2P → apply-a2p disappears,
  CNP-verified → a2p-approved flips on, and number-provisioned → hide
  all happen WITHOUT a hard page reload. Phone verify still does a
  window.location.reload on success (see onVerified below) because the
  surrounding inbox UI needs the freshly-created self-thread anyway.

  Note on TFN: the trial_survey_number_type column captures the user's
  TFN-vs-10DLC preference at survey time. The trial header itself doesn't
  branch on it today — that signal feeds the post-upgrade number picker.
*/

type FunnelState =
  | 'verify-phone'
  | 'apply-a2p'
  | 'a2p-pending'
  | 'a2p-approved'
  // TFN-preferring users bypass the three A2P stages entirely.
  | 'upgrade-for-tfn'
  | 'paid-choose-number'
  | 'expired'
  | 'hide';

interface BannerConfig {
  copy: string;
  ctaLabel: string;
  ctaHref: string;
}

export const TrialHeader = () => {
  const { isTrial, subdomain } = useContext(AccountInfoContext);
  const { phoneValid } = useContext(UserInfoContext);
  const { trialEndDate } = useContext(OrgInfoContext);
  // hasTcrBrand is also exposed via AccountInfoContext but that context is
  // seeded at page load and doesn't auto-refresh. Read the brand from the
  // redux selector instead so the funnel state advances from
  // 'apply-a2p' → 'a2p-pending' the moment registerTcrBrand dispatches,
  // without requiring a hard page reload.
  const tcrBrand = useSelector(tcrBrandSelector);
  const tcrCampaigns = useSelector(tcrCampaignsSelector) || {};
  const phoneNumbers = useSelector(orgPhoneNumbersSelector) || [];
  const trialSurveyNumberType = useSelector(orgTrialSurveyNumberTypeSelector);
  const orgId = useSelector(orgIdSelector);
  const prefersTfn = trialSurveyNumberType === 'tfn';
  const { navigate } = useNavigationActions();
  const dispatch = useDispatch();
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const hasTcrBrand = !!tcrBrand;

  // Ensure org_props + tcr_props are loaded into redux. The pages that
  // CARE about TCR (A2P forms, billing, AvoAI) each call these hooks,
  // but the inbox doesn't — so without this, a trial user landing on
  // /tickets has a stale redux store where trialSurveyNumberType and
  // tcrBrand are still at their initial-state nulls. That causes the
  // funnel state machine to fall through to 'apply-a2p' even when the
  // user is on the TFN path. Same idempotent fetch-once-on-mount
  // pattern as useFetchOrgProps / useFetchTcrProps — both gate on the
  // "is the data already loaded?" check so the dispatch is a no-op
  // for users on pages that already loaded them.
  useEffect(() => {
    if (subdomain && !orgId) dispatch(getOrgProps({ subdomain }));
  }, [dispatch, subdomain, orgId]);
  useEffect(() => {
    if (subdomain && !tcrBrand?.id) dispatch(fetchTcrProps({ subdomain }));
  }, [dispatch, subdomain, tcrBrand?.id]);

  // CNP / DCA_COMPLETE = TCR campaign is fully approved by all carriers.
  // Surfaces as external_campaign_status === 'VERIFIED' on any campaign
  // (see Twilio::SyncExternalCampaignStatusBatchJob).
  const hasApprovedCampaign = Object.values(tcrCampaigns).some(
    (c: any) => c?.external_campaign_status === 'VERIFIED',
  );

  const trialDaysLeft = useMemo(() => {
    if (!trialEndDate) return 0;
    const ms = new Date(trialEndDate).getTime() - new Date().getTime();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [trialEndDate]);

  const state: FunnelState = useMemo(() => {
    if (!isTrial) {
      return phoneNumbers.length === 0 ? 'paid-choose-number' : 'hide';
    }
    if (trialDaysLeft === 0) return 'expired';
    if (!phoneValid) return 'verify-phone';
    // TFN users skip the three A2P stages entirely — the TFN compliance
    // form only opens once they have a TFN number provisioned, which
    // requires upgrading first.
    if (prefersTfn) return 'upgrade-for-tfn';
    if (!hasTcrBrand) return 'apply-a2p';
    if (hasApprovedCampaign) return 'a2p-approved';
    return 'a2p-pending';
  }, [isTrial, phoneValid, hasTcrBrand, hasApprovedCampaign, prefersTfn, trialDaysLeft, phoneNumbers.length]);

  if (state === 'hide') return null;

  const upgradeUrl = `${window.location.origin}/accounts/${subdomain}/billing_v2/upgrade`;
  const chooseNumberUrl = `${window.location.origin}/accounts/${subdomain}/number`;
  const applyA2pUrl = `${window.location.origin}/accounts/${subdomain}/tcr/first_submission`;
  const a2pStatus = `${window.location.origin}/accounts/${subdomain}/tcr`;
  // 'verify-phone' state opens the inline TrialPhoneVerifyModal below
  // instead of navigating — `ctaHref: ''` is a sentinel handled in handleCta.
  const CONFIG: Record<FunnelState, BannerConfig> = {
    'verify-phone':       { copy: 'Get $5 in free balance to text',     ctaLabel: 'Verify Phone',  ctaHref: ''              },
    'apply-a2p':          { copy: 'Get A2P approved for free to text',  ctaLabel: 'Apply for A2P', ctaHref: applyA2pUrl     },
    'a2p-pending':        { copy: 'A2P is pending carrier review',      ctaLabel: 'See Status',    ctaHref: a2pStatus       },
    'a2p-approved':       { copy: 'A2P is approved, upgrade to text',   ctaLabel: 'Upgrade',       ctaHref: upgradeUrl      },
    // TFN funnel: skip A2P, upgrade to pick a toll-free number. The
    // verify-TFN button appears on the number row after provisioning.
    'upgrade-for-tfn':    { copy: 'Upgrade to pick a toll-free number', ctaLabel: 'Upgrade',       ctaHref: upgradeUrl      },
    'paid-choose-number': { copy: 'Choose a number to start texting',   ctaLabel: 'Choose Number', ctaHref: chooseNumberUrl },
    'expired':            { copy: 'Trial ended, see our pricing plans', ctaLabel: 'Upgrade',       ctaHref: upgradeUrl      },
    'hide':               { copy: '', ctaLabel: '', ctaHref: '' },
  };

  const config = CONFIG[state];

  const handleCta = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (state === 'verify-phone') {
      setShowVerifyModal(true);
      return;
    }
    navigate({ path: config.ctaHref, params: {} });
  };

  return (
    <Wrapper>
      <Pill>
        <Copy>{config.copy}</Copy>
        <CtaButton type="button" onClick={handleCta}>
          {config.ctaLabel}
        </CtaButton>
      </Pill>
      {/*
        Verify-phone modal lives inside the header so the CTA opens it
        regardless of which page the user is on. On success, drop them
        into their newly-created self-thread (same redirect logic the
        TicketView + Onboarding instances use).
      */}
      {state === 'verify-phone' && (
        <TrialPhoneVerifyModal
          show={showVerifyModal}
          onClose={() => setShowVerifyModal(false)}
          onVerified={(result: any) => {
            if (result?.selfTicketUuid && result?.subdomain) {
              window.location.href = `/accounts/${result.subdomain}/tickets/${result.selfTicketUuid}`;
              return;
            }
            window.location.reload();
          }}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 10px;
`;

const Pill = styled.div`
  position: relative;
  background: #FBF8F3;
  padding: 5px 5px 5px 14px;
  height: 41px;
  border: 1px solid #DAD7D6;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
`;

const Copy = styled.span`
  white-space: nowrap;
  @media (max-width: 768px) {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CtaButton = styled.button`
  background: #19BE51;
  color: #fff;
  border: none;
  padding: 2px 14px;
  height: 30px;
  min-width: 100px;
  border-radius: 50px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0px 2px 6px rgb(0 0 0 / 25%);
  &:hover { background: #16a847; }
`;
